import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import DonutChart from "Components/DonutChart";
import SegmentControl from "Components/Sales/SegmentControl";
import BreakdownBarChart from "Components/Sales/charts/BreakdownBarChart";
import { MONTH_LABELS, formatWon, formatKoreanUnit } from "Components/Sales/salesFormat";
import useSalesFilter from "utils/useSalesFilter";
import unwrapResponse from "utils/unwrapResponse";
import { catchError } from "JS/common";
import { getSalesBreakdown } from "JS/API";

const METRIC_OPTIONS = [
  { key: "gross", label: "매출" },
  { key: "net", label: "순매출" },
];
const MODE_OPTIONS = [
  { key: "amt", label: "금액" },
  { key: "pct", label: "비율 (100%)" },
];

const styles = {
  toolbar: {
    display: "flex",
    gap: "14px",
    alignItems: "center",
    flexWrap: "wrap",
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    padding: "14px 18px",
    marginBottom: "20px",
  },
  yearNav: { display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", fontSize: "16px", color: "#1f2937" },
  navButton: {
    width: "28px", height: "28px", backgroundColor: "#f3f4f6", border: "none", borderRadius: "6px",
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280",
  },
  label: { fontSize: "13px", color: "#6b7280" },
  grid: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) 340px", gap: "20px" },
  card: { backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "22px 24px" },
  cardTitle: { fontSize: "16px", fontWeight: "600", color: "#1f2937", margin: "0 0 4px" },
  cardDesc: { fontSize: "13px", color: "#6b7280", margin: "0 0 12px" },
  sideList: { display: "flex", flexDirection: "column", gap: "4px" },
  sideRow: {
    display: "grid", gridTemplateColumns: "12px 1fr auto auto", gap: "10px", alignItems: "center",
    fontSize: "13px", padding: "6px 4px", borderBottom: "1px solid #f1f5f9", fontVariantNumeric: "tabular-nums",
  },
  swatch: { width: "12px", height: "12px", borderRadius: "3px", display: "inline-block" },
  pct: { color: "#6b7280", minWidth: "72px", textAlign: "right" },
  empty: { padding: "24px 0", color: "#9ca3af", fontSize: "13px", textAlign: "center" },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", padding: "80px", color: "#9ca3af", fontSize: "14px" },
};

const SalesBreakdownTab = ({ service }) => {
  const navigate = useNavigate();
  const { year, updateParams } = useSalesFilter();

  const [metric, setMetric] = useState("gross");
  const [mode, setMode] = useState("amt");
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({ mode: "", context: "", bool: false });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getSalesBreakdown(year, service, metric);
      if (typeof result !== "object" || !result?.data) {
        return catchError(result, navigate, setAlertBox, setAlert);
      }
      const next = unwrapResponse(result);
      setData(next);
      setSelectedMonth((prev) => {
        const withData = (next?.months || []).filter((m) => m.total > 0);
        if (prev && (next?.months || []).some((m) => m.month === prev)) return prev;
        return withData.length ? withData[withData.length - 1].month : (next?.months?.length || 1);
      });
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    } finally {
      setIsLoading(false);
    }
  }, [year, service, metric, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAlertOk = useCallback(() => {
    if (alert === "logout") navigate("/");
  }, [alert, navigate]);

  const tagMap = useMemo(() => new Map((data?.tags || []).map((t) => [t.id, t])), [data]);
  const monthDetail = useMemo(() => {
    const m = (data?.months || []).find((x) => x.month === selectedMonth);
    if (!m) return { total: 0, rows: [] };
    return {
      total: m.total,
      rows: m.by_tag.map((b) => ({ ...b, tag: tagMap.get(b.tag_id) })).filter((r) => r.tag),
    };
  }, [data, selectedMonth, tagMap]);
  const yearRows = useMemo(
    () => (data?.year_total?.by_tag || []).map((b) => ({ ...b, tag: tagMap.get(b.tag_id) })).filter((r) => r.tag),
    [data, tagMap]
  );
  const metricLabel = metric === "net" ? "순매출" : "매출";

  return (
    <div>
      <div style={styles.toolbar}>
        <div style={styles.yearNav}>
          <button type="button" aria-label="이전 연도" style={styles.navButton} onClick={() => updateParams({ year: year - 1 })}>
            <FaChevronLeft size={12} />
          </button>
          <span>{year}년</span>
          <button type="button" aria-label="다음 연도" style={styles.navButton} onClick={() => updateParams({ year: year + 1 })}>
            <FaChevronRight size={12} />
          </button>
        </div>
        <span style={styles.label}>지표</span>
        <SegmentControl options={METRIC_OPTIONS} value={metric} onChange={setMetric} compact />
        <span style={styles.label}>표시</span>
        <SegmentControl options={MODE_OPTIONS} value={mode} onChange={setMode} compact />
      </div>

      {isLoading || !data ? (
        <div style={styles.loading}>로딩 중...</div>
      ) : (
        <div style={styles.grid}>
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>월별 태그 비중</h2>
            <p style={styles.cardDesc}>{metricLabel} 기준 · 막대를 누르면 해당 월 상세가 오른쪽에 표시됩니다</p>
            {data.tags.length === 0 ? (
              <div style={styles.empty}>{year}년 {metricLabel} 데이터가 없습니다.</div>
            ) : (
              <BreakdownBarChart
                months={data.months}
                tags={data.tags}
                mode={mode}
                selectedMonth={selectedMonth}
                onSelectMonth={setSelectedMonth}
              />
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={styles.card}>
              <h2 style={styles.cardTitle}>{year}년 {selectedMonth ? MONTH_LABELS[selectedMonth - 1] : ""} 상세</h2>
              <p style={styles.cardDesc}>합계 {formatWon(monthDetail.total)}</p>
              {monthDetail.rows.length === 0 ? (
                <div style={styles.empty}>데이터가 없습니다.</div>
              ) : (
                <div style={styles.sideList} data-testid="month-detail">
                  {monthDetail.rows.map((r) => (
                    <div key={r.tag_id} style={styles.sideRow}>
                      <span style={{ ...styles.swatch, backgroundColor: r.tag.color }} />
                      <span>{r.tag.name}</span>
                      <span>{formatWon(r.amount)}</span>
                      <span style={styles.pct}>{monthDetail.total ? Math.round((r.amount / monthDetail.total) * 100) : 0}% · {r.count}건</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.card}>
              <h2 style={styles.cardTitle}>{year}년 연간 비중</h2>
              <p style={styles.cardDesc}>누적 {metricLabel} {formatKoreanUnit(data.year_total.total)} 원</p>
              <DonutChart unit="원" data={yearRows.map((r) => ({ label: r.tag.name, count: r.amount, color: r.tag.color }))} />
            </div>
          </div>
        </div>
      )}

      {alertBox.bool && (
        <CommonModal setModal={setAlertBox} modal={alertBox} okFn={handleAlertOk} />
      )}
    </div>
  );
};

export default SalesBreakdownTab;
